/**
 * Authentication Context
 *
 * Manages user session state across the app.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { track } from '../lib/analytics';

export type UserTier = 'free' | 'pro';

export interface User {
  id: string;
  email: string;
  name: string | null;
  email_verified: boolean;
  avatar_url?: string | null;
  tier: UserTier;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** null = unknown (existing session); false = the signup email failed to send */
  verificationEmailSent: boolean | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string, name?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Request a fresh verification email for the signed-in user */
  resendVerification: () => Promise<{ sent: boolean; message: string }>;
  /** Apply the user returned by /verify-email (signs in when needed) */
  applyVerifiedUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Relative URLs: the Vite dev server proxies /api to the Express API (vite.config.ts); production serves both from one host.
const API_BASE = '';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [verificationEmailSent, setVerificationEmailSent] = useState<boolean | null>(null);

  // Check for existing session on mount
  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        return { success: false, error: data.error || 'Login failed' };
      }

      setUser(data.user);
      return { success: true };
    } catch {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, []);

  const signup = useCallback(async (email: string, password: string, name?: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name }),
      });

      const data = await res.json();

      if (!res.ok) {
        return { success: false, error: data.error || 'Signup failed' };
      }

      // Signed in immediately (server sets the session cookie)
      setUser(data.user);
      setVerificationEmailSent(data.verification_email_sent !== false);
      track('signup-complete', { source: 'form' });
      return { success: true };
    } catch {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, []);

  const resendVerification = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        if (data.already_verified) {
          await refreshUser();
          return { sent: false, message: 'Your email is already verified.' };
        }
        setVerificationEmailSent(true);
        return { sent: true, message: 'Sent — check your inbox.' };
      }
      return { sent: false, message: data.error || 'Could not send the email.' };
    } catch {
      return { sent: false, message: 'Network error. Please try again.' };
    }
  }, [refreshUser]);

  const applyVerifiedUser = useCallback((verified: User) => {
    setUser(verified);
    setVerificationEmailSent(null);
    track('verify-complete', { source: 'email' });
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors - clear local state anyway
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, verificationEmailSent, login, signup, logout, refreshUser, resendVerification, applyVerifiedUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
